import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useAuthentication } from "features/Authentication/context";
import { useUser } from "utils/hooks/user";
import useApi from "utils/api";

type User = Record<string, any>;

export const useProfile = () => {
    const { id } = useParams();
    const loggedUser = useUser();
    const queryClient = useQueryClient();
    const { dispatch } = useAuthentication();
    const { authGet } = useApi();

    // Shares the same cache key as useProfileQuery — no duplicate network request.
    // staleTime: Infinity means this instance never independently triggers a refetch;
    // useProfileQuery owns the fetching. If the cache is empty (e.g. this runs first),
    // it falls back to fetching itself with the same result.
    const { data: user } = useQuery<any>({
        queryKey: [`/users/${id}`],
        queryFn: ({ queryKey }) => authGet(queryKey[0] as string),
        enabled: !!id,
        staleTime: Infinity,
    });

    const updateUser = useCallback((updated: Partial<User>) => {
        if (id) {
            queryClient.setQueryData([`/users/${id}`], (prev: any) => ({
                ...(prev || {}),
                ...updated,
            }));
        } else if (loggedUser) {
            dispatch({
                type: "set-user",
                payload: { user: { ...loggedUser, ...updated } as User },
            });
        }
    }, [id, loggedUser, dispatch, queryClient]);

    return useMemo(() => ({
        user: user || loggedUser,
        isSelf: !id,
        updateUser,
    }), [user, loggedUser, id, updateUser]);
}
